import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData, Scope, showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { useApiErrorHandler } from '../../../useApiErrorHandler'

export interface CreateGhostProfileData {
  scope?: Scope
  residence?: string
  residenceHistory?: string[]
  birthDate?: string
  bio?: string
  company?: string
  firstName?: string
  gender?: 'male' | 'female' | 'non-binary'
  hashtags?: string[]
  pinsHashtags?: string[]
  pinsCategories?: string[]
  goals?: string[]
  hobbies?: string[]
  skills?: string[]
  professionIds?: string[]
  languages?: string[]
  lastName?: string
  nationalities?: string[]
  origins?: string[]
  contacts?: {
    email?: string | null
    phone?: string | null
    website?: string | null
    facebook?: string | null
    instagram?: string | null
    twitter?: string | null
    linkedin?: string | null
  }
  availabilities?: {
    momentIds: string[]
    slots: Array<{
      days: number[]
      from: string
      to: string
    }>
  }
  avatar?: {
    uri: string
    type?: string
  }
}

interface CreateGhostProfileResponse {
  id: string
}

export const createGhostProfile = async (data: CreateGhostProfileData): Promise<string> => {
  const { avatar, contacts, languages, nationalities, availabilities, ...otherData } = data
  const formData = new FormData()

  // Add simple fields
  if (otherData.scope) {
    formData.append('scope', otherData.scope)
  }
  if (otherData.residence) {
    formData.append('residence', otherData.residence)
  }
  if (otherData.birthDate) {
    formData.append('birthDate', otherData.birthDate)
  }
  if (otherData.bio) {
    formData.append('bio', otherData.bio)
  }
  if (otherData.company !== undefined) {
    formData.append('company', otherData.company)
  }
  if (otherData.firstName) {
    formData.append('firstName', otherData.firstName)
  }
  if (otherData.lastName) {
    formData.append('lastName', otherData.lastName)
  }
  if (otherData.gender) {
    formData.append('gender', otherData.gender)
  }
  if (otherData.origins) {
    if (otherData.origins.length > 0) {
      otherData.origins.forEach((origin, index) => {
        formData.append(`origins[${index}]`, origin)
      })
    } else {
      formData.append('origins', JSON.stringify([]))
    }
  }

  // Handle hashtags
  if (otherData.hashtags && otherData.hashtags.length > 0) {
    otherData.hashtags.forEach((tag, index) => {
      formData.append(`hashtags[${index}]`, tag)
    })
  }

  if (otherData.pinsHashtags !== undefined) {
    if (otherData.pinsHashtags.length > 0) {
      otherData.pinsHashtags.forEach((tag, index) => {
        formData.append(`pinsHashtags[${index}]`, tag)
      })
    } else {
      formData.append('pinsHashtags', JSON.stringify([]))
    }
  }

  if (otherData.pinsCategories !== undefined) {
    if (otherData.pinsCategories.length > 0) {
      otherData.pinsCategories.forEach((category, index) => {
        formData.append(`pinsCategories[${index}]`, category)
      })
    } else {
      formData.append('pinsCategories', JSON.stringify([]))
    }
  }

  // Handle goals, hobbies, skills
  if (otherData.goals && otherData.goals.length > 0) {
    otherData.goals.forEach((goal, index) => {
      formData.append(`goals[${index}]`, goal)
    })
  }
  if (otherData.hobbies && otherData.hobbies.length > 0) {
    otherData.hobbies.forEach((hobby, index) => {
      formData.append(`hobbies[${index}]`, hobby)
    })
  }
  if (otherData.skills && otherData.skills.length > 0) {
    otherData.skills.forEach((skill, index) => {
      formData.append(`skills[${index}]`, skill)
    })
  }

  // Handle languages
  if (languages && languages.length > 0) {
    languages.forEach((language, index) => {
      formData.append(`languages[${index}]`, JSON.stringify({ code: language, level: 'fluent' }))
    })
  } else {
    formData.append('languages', JSON.stringify([]))
  }

  // Handle nationalities
  if (nationalities && nationalities.length > 0) {
    nationalities.forEach((nationality, index) => {
      formData.append(`nationalities[${index}]`, nationality)
    })
  }

  // Handle professionIds
  if (otherData.professionIds && otherData.professionIds.length > 0) {
    otherData.professionIds.forEach((professionId, index) => {
      formData.append(`professionIds[${index}]`, professionId)
    })
  } else {
    formData.append('professionIds', JSON.stringify([]))
  }

  // Handle residenceHistory
  if (otherData.residenceHistory && otherData.residenceHistory.length > 0) {
    const residenceHistoryArray = otherData.residenceHistory.map((residence) => ({
      city: residence,
      since: new Date().toISOString(),
      until: null,
      address: '',
      country: null,
      isCurrent: false,
    }))
    formData.append('residenceHistory', JSON.stringify(residenceHistoryArray))
  }

  // Handle contacts
  if (contacts) {
    formData.append('contacts', JSON.stringify(contacts))
  }

  // Handle availabilities
  if (availabilities) {
    formData.append('availabilities', JSON.stringify(availabilities))
  }

  // Handle avatar
  if (avatar) {
    appendAvatarToFormData(formData, avatar.uri, 'avatar')
  }

  const { data: response } = await api.post<CreateGhostProfileResponse>(
    '/ghost-profiles',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )

  return response.id
}

export const useCreateGhostProfile = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const handleApiError = useApiErrorHandler()

  return useMutation<string, Error, CreateGhostProfileData>({
    mutationFn: createGhostProfile,
    onSuccess: async () => {
      showSuccessToast(t('toast.success'), t('ghostProfile.createSuccess'))
      // Invalidate contacts query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    // A free account at its contact cap gets 402 PLAN_LIMIT_CONTACTS_REACHED →
    // show the paywall instead of a raw error toast.
    onError: handleApiError,
  })
}
